import React from 'react';
import { Mail, Linkedin } from 'lucide-react';
import jamesPortrait from '@/assets/partner-james-mwangi.jpg';
import SEO from '../../components/SEO';

const JamesMwangi = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="James Mwangi — Law and Code Africa"
        description="James Mwangi at Law and Code Africa. LLB Hons, CPM, Trainee Advocate. Focused on competition law, corporate governance, and commercial arbitration."
        path="/partners/james-mwangi"
      />

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-primary">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">James Mwangi</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              LLB Hons | CPM | Trainee Advocate
            </p>
            <div className="max-w-6xl mx-auto text-lg text-blue-100 space-y-4 text-justify px-4">
              <p>
                James Mwangi is a commercially minded legal analyst with primary areas of interest in Competition Law, Corporate Governance, and Commercial Arbitration. A Trainee Advocate currently enrolled at the Kenya School of Law, he brings a rigorous understanding of market regulation and board-level accountability to the commentary produced at Law and Code Africa.
              </p>
              <p>
                He has demonstrated a strong aptitude for dissecting complex merger control assessments, abuse-of-dominance inquiries, and public–private governance frameworks. Through moot court competitions, policy workshops, and active engagement with the Law Society of Kenya, he continues to sharpen his analytical and advocacy skills.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <div className="relative overflow-hidden rounded-lg shadow-lg w-full max-w-sm mb-6 bg-gray-50">
                <img
                  src={jamesPortrait}
                  alt="James Mwangi - Partner, Law and Code Africa"
                  loading="lazy"
                  className="w-full h-auto object-cover transition-all duration-700 hover:scale-110 hover:brightness-110"
                />
              </div>
              <div className="bg-gray-50 p-5 md:p-6 rounded-lg w-full max-w-sm">
                <h3 className="text-lg md:text-xl font-bold text-primary mb-4 text-center">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="text-secondary flex-shrink-0" size={20} />
                    <span className="text-gray-700 text-sm md:text-base break-all">lawandcodeafrica@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    <Linkedin className="text-secondary flex-shrink-0" size={20} />
                    <a
                      href="https://www.linkedin.com/in/james-mwangi-law"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm md:text-base break-all"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-primary mb-8">Background and Qualifications</h2>
            <div className="space-y-6 text-gray-700">
              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Educational Foundation</h3>
                <div className="space-y-3">
                  <p>
                    <strong>University of Nairobi, School of Law (LLB Hons):</strong> Attended the University of Nairobi where he developed a foundation in constitutional, commercial, and administrative law. During his tenure, he served as Secretary-General of the UoN Mooting Society and represented the university at the African Human Rights Moot Court Competition.
                  </p>
                  <p>
                    <strong>Kenya School of Law:</strong> Currently an Advocates Training Programme (ATP) student.
                  </p>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Qualifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Professional Mediator</h4>
                    <p className="text-sm text-gray-600">Holds certification as a Professional Mediator</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Competition Analyst</h4>
                    <p className="text-sm text-gray-600">Skilled in merger assessments, dominance analysis, and regulatory compliance</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Experience &amp; Community Involvement</h3>
                <div className="space-y-3">
                  <p>
                    <strong>Competition Authority of Kenya:</strong> Undertook a research attachment reviewing market-inquiry reports and leniency applications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Specialization */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center">
            Key Practice Areas and Interests
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-4xl mx-auto">
            James Mwangi's professional interests and developing specializations encompass:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Competition Law</h3>
              <p className="text-gray-600">
                Merger control, abuse of dominance, cartel enforcement, and the interplay between digital markets and antitrust regulation in Africa.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Corporate Governance</h3>
              <p className="text-gray-600">
                Board accountability, ESG disclosures, shareholder rights, and governance frameworks for publicly listed and private entities.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Commercial Arbitration</h3>
              <p className="text-gray-600">
                Arbitration clauses, enforcement of arbitral awards, and alternative dispute resolution in cross-border commercial transactions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JamesMwangi;
